import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TSIconModule } from '../ts-icon/ts-icon.module';

/** Named color variants (mirroring ts-button's `color`) or any arbitrary CSS color / hex code. */
export type TsAccordionIconColor = 'primary' | 'basic' | 'accent' | 'warn' | 'success' | (string & {});

/** Theme colors backing the named variants. Any other value is passed through as-is (e.g. a hex code). */
const ICON_COLOR_VARIANTS: Record<'primary' | 'basic' | 'accent' | 'warn' | 'success', string> = {
  primary: '#207CEC',
  basic: '#6B7280',
  accent: '#C27803',
  warn: '#C81E1E',
  success: '#0E9F6E',
};

/**
 * A single collapsible panel. Place one or more inside `<ts-accordion>`; when the
 * parent accordion is single-open (`[multi]="false"`, the default) the items
 * coordinate so only one is expanded at a time. Body content is projected.
 */
@Component({
  selector: 'ts-accordion-item',
  standalone: true,
  imports: [NgClass, CdkAccordionModule, MatTooltipModule, TSIconModule],
  templateUrl: './ts-accordion-item.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TsAccordionItemComponent {
  readonly title = input<string | undefined>();
  readonly description = input<string | undefined>();
  readonly expanded = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  /** Icon shown before the title (e.g. a status indicator for the title). */
  readonly titleIcon = input<string | undefined>();
  readonly titleIconColor = input<TsAccordionIconColor | undefined>();

  /** Icon shown after the description (e.g. a status indicator for the content). */
  readonly contentIcon = input<string | undefined>();
  readonly contentIconColor = input<TsAccordionIconColor | undefined>();
  /** Tooltip for the content icon. */
  readonly contentIconTooltip = input<string | undefined>();

  /** Resolves a named variant to its theme color; passes arbitrary hex/CSS colors through unchanged. */
  resolveIconColor(color: TsAccordionIconColor | undefined): string | undefined {
    if (!color) {
      return undefined;
    }

    return ICON_COLOR_VARIANTS[color as keyof typeof ICON_COLOR_VARIANTS] ?? color;
  }
}
